'use client';

import styles from './CustomerCard.module.css';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@app/ui/_lib/utils';
import { isValidRichText } from '../_lib/isValidRichText';
import { Media } from '../Media';
import { PanelStory, type PanelStoryProps } from '../Panel';
import { Popup } from '../Popup';
import { Text } from '../Text';
import type { Brands } from '../Theme';
import { useBrandValue } from '../Theme';
import { IconResolver } from '../IconResolver';
import type { SanityImage } from '../types';

export type CustomerCardProps = {
	className?: string;
	copy: ReactNode;
	image?: SanityImage;
	label?: string;
	link: {
		label: string;
	};
};

export function CustomerCard(props: CustomerCardProps) {
	return (
		<article
			className={cn(
				`group/customerCard h-full flex relative bg-card-background-tertiary border border-border-secondary rounded-lg px-6 pb-6 pt-5`,
				props.link ? 'transition-colors duration-normal ease-smooth hover:bg-card-background-tertiary-hover' : '',
				props.className,
			)}
			data-component='CustomerCard'
		>
			{props.label && (
				<Text className={`text-text-brand absolute top-3 left-4 z-10`} styleType='label-sm'>
					{props.label}
				</Text>
			)}
			<div
				className={`flex flex-col transition-transform duration-normal ease-smooth translate-y-5 group-hover/customerCard:translate-y-0`}
			>
				<Media
					className={cn(styles.customerCard, 'aspect-2/1 w-[9.375rem] mx-auto mb-3 [&_img]:h-full [&_img]:object-contain!')}
					image={props.image}
				/>
				<div className={`mt-auto flex flex-col gap-4 text-center items-center`}>
					{isValidRichText(props.copy) && (
						<Text className={`opacity-70 w-full`} styleType='body-xs-medium'>
							{props.copy}
						</Text>
					)}
					{props.link && (
						<Text
							as='span'
							className={`text-text-brand inline-flex gap-1 transition-opacity duration-normal ease-smooth opacity-0 group-hover/customerCard:opacity-100`}
							styleType='button-sm'
						>
							{props.link.label}
							<IconResolver className={`w-[0.75rem]! h-[0.75rem]!`} icon='plus' />
						</Text>
					)}
				</div>
			</div>
		</article>
	);
}

export type CustomerCardPopupProps = {
	card: CustomerCardProps;
	defaultBrand?: Brands;
	panel: PanelStoryProps;
	setPauseTicker?: Function;
};

export function CustomerCardPopup(props: CustomerCardPopupProps) {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (props.setPauseTicker) {
			props.setPauseTicker(open);
		}
	}, [open]);

	return (
		<Popup
			brand={props.defaultBrand ?? useBrandValue()}
			open={open}
			setOpen={setOpen}
			layout={'sidebar'}
			triggerButton={
				<button className='w-full h-full' onClick={() => setOpen(true)}>
					<CustomerCard {...props.card} />
				</button>
			}
		>
			{props.panel && <PanelStory {...props.panel} />}
		</Popup>
	);
}
